<template>
  <div class="p-envelope-status__loader" v-if="envelope.loading">
    <VLoadingSpinner size="large" />
  </div>
  <div class="p-envelope-status g-page" v-else>
    <div v-if="viewingEnvelope && viewingEnvelope.id">
      <div class="g-page__header">
        <div class="flex flex-col">
          <h2 data-hj-suppress>{{ viewingEnvelope.title }}</h2>
          <span class="p-envelope-status--header-details">
            <VLabel :type="viewingEnvelope.status">
              <template v-slot:text>
                {{ $t(`envelope.status.${viewingEnvelope.status}`) }}
              </template>
            </VLabel>
            <VMessage type="warning" subtype="basic" v-if="viewingEnvelope.status === 'sent'">
              <template v-slot:icon>
                <ExclamationTriangleIcon />
              </template>
              <template v-slot:text>
                {{ $t('envelope.messageNotYetProcessed') }}
              </template>
            </VMessage>
            <VMessage type="success" subtype="basic" v-if="viewingEnvelope.status === 'signed'">
              <template v-slot:icon>
                <CheckIcon />
              </template>
              <template v-slot:text>
                {{ $t('envelope.messageSignedByAll') }}
              </template>
            </VMessage>
            <VMessage type="warning" subtype="basic" v-if="viewingEnvelope.status === 'expired'">
              <template v-slot:icon>
                <ExclamationTriangleIcon />
              </template>
              <template v-slot:text>
                {{ $t('envelope.messageExpired') }}
              </template>
            </VMessage>
          </span>
          <label class="mt-4 text-14 font-normal" v-if="showEditSharing">
            <span v-if="sharedWithEveryone" data-test="shared-with-everyone-text">
             {{ $t('envelope.sharedWithEveryone') }} | <a @click="openEditSharingModal" data-ec="envelope_status" data-et="anchor_open_edit_sharing_modal">{{ $t('envelope.editSharing') }}</a>
            </span>
            <span v-else data-test="shared-with-actors">
            {{ $tc('sharing.sharedWithCount', null, { groupCount: sharedGroups.length, userCount: sharedUsers.length }) }} | <a @click="openEditSharingModal" data-ec="envelope_status" data-et="anchor_open_edit_sharing_modal">{{ $t('envelope.editSharing') }}</a>
            </span>
          </label>
        </div>
      </div>
      <VGrid>
        <div slot="gridItem" type="9">
          <div class="g-page__row" v-if="$store.getters['envelopes/viewing'].next_remind > 0">
            <VMessage type="info" subtype="outlined" :fullwidth="true">
              <template v-slot:icon>
                <InfoCircleIcon />
              </template>
              <template v-slot:text>
                {{ $t('envelope.messageRemindersOccur.a') }}
                {{ $tc('envelope.messageRemindersOccur.b', remindDays, { remindDays }) }}
                {{ $t('envelope.messageRemindersOccur.c') }}
                {{ viewingEnvelope.next_remind | formatDate('calendar') }}.
              </template>
            </VMessage>
          </div>
          <div class="g-page__row" v-if="$store.getters['envelopes/viewing'].next_expire && $store.getters['envelopes/viewing'].next_expire > 0">
            <VMessage type="info" subtype="outlined" :fullwidth="true">
              <template v-slot:icon>
                <InfoCircleIcon />
              </template>
              <template v-slot:text>
                {{ $t('envelope.messageExpiryOccurs.a') }}
                {{ $store.getters['envelopes/viewing'].next_expire | formatDate('calendar')}}
                {{ $t('envelope.messageExpiryOccurs.b') }}
              </template>
            </VMessage>
          </div>
          <div class="g-page__row">
            <h3>{{ $t('envelope.signingStatus') }}</h3>
            <PartyStatusTable :loading="envelope.loading" />
          </div>
          <div class="g-page__row" v-if="!!viewingEnvelope.parties && viewingEnvelope.parties.length > 0">
            <h3>{{ $t('envelope.envelopeFields') }} -
              <a
                @click="downloadFieldData"
                data-ec="envelope_status"
                data-et="fields_csv_download">
                {{ $t('envelope.downloadFieldData') }}
              </a>
            </h3>
            <div class="g-page__row"
              v-for="party in viewingEnvelope.parties"
              :key="party.id">
              <b data-hj-suppress>{{ party.title }} - {{ party.email }}</b>
              <VTable
                :loading="envelope.loading"
                :thead="[
                  { value: $t('envelope.field.title'), width: 40 },
                  { value: $t('envelope.field.type'), width: 20 },
                  { value: $t('envelope.field.value'), width: 40 },
                ]"
                :no-results-message="`There are no fields assigned to ${party.title}`"
                :tbody="$store.getters['envelopes/fieldsForParty'](party.id, true)"
                :columns="[
                  'title',
                  'type',
                  'content',
                ]"
                :stringFormatter="{
                  content: fieldValueFormatter,
                  title: titleFormatter,
                }"
                :actions="true"
                :collapse="true"
                data-hj-suppress>
                <template slot-scope="field">
                  <button
                    v-if="field.type === 'upload' && uploadHasContent(field)"
                    type="button"
                    class="tw-button tw-button-small tw-button-rounded"
                    :class="{ 'tw-button-loading': uploadButtonLoadingArray.includes(field.id) }"
                    @click="downloadUploadFieldFiles(field.id)"
                    data-ec="envelope_status"
                    data-et="field_content_download">
                    <DownloadIcon />
                    <span>{{ $t('functional.download') }}</span>
                  </button>
                </template>
              </VTable>
            </div>
          </div>
          <ExtendableFeed
              :extendable="false"
              :title="$t('ui.envelopes.envelopeHistory.title')"
              :history="viewingLog"
              :itemType="$t('ui.envelopes.envelopeHistory.envelope')"
              ec="envelope_status_envelope_history"
          />
        </div>
        <div slot="gridItem" type="3">
          <VStickyColumn
            relativeElementSelector=".o-grid__item--relative-sticky-column"
            pageClass=".p-envelope-status">
            <img
            class="p-envelope-status__preview"
            src="@/assets/loading.png"
            v-if="viewingEnvelope.documents"
            v-image-loader="{
                src: firstPageImg,
                onerror: require('@/assets/error.png'),
            }" />
            <div class="g-page__row p-envelope-status__controls">
              <DownloadFileButton
                v-if="viewingEnvelopeIsDownloadable"
                :params="{ fingerprint: viewingEnvelope.fingerprint}"
                :file-name="viewingEnvelope.title"
                variant="secondary"
                data-ec="envelope_status"
                data-et="controls_download_envelope"
                url="download/envelope"
                class="w-full mb-base"
              />
              <IconButton
                  id="envelope-status-continue-draft-cta"
                  @click="$router.push({ name: 'EnvelopeCreate', params: { envelopeFingerprint: viewingEnvelope.fingerprint } });"
                  v-if="viewingEnvelope.status === 'draft' && canEditResource(viewingEnvelope)"
                  data-test="button-continue-creating"
                  data-ec="envelope_status"
                  data-et="controls_contine_draft"
                  class="mb-base w-full"
                  icon="pencil"
                  variant="secondary">
                <span>{{ $t('envelope.continueDraft') }}</span>
              </IconButton>
              <IconButton
                v-if="showEditSharing"
                id="envelope-status-edit-sharing-cta"
                @click="openEditSharingModal"
                data-ec="envelope_status"
                data-et="btn_open_edit_sharing_modal"
                class="mb-base w-full"
                icon="user"
                variant="secondary">
                <span>{{ $t('envelope.editSharing') }}</span>
              </IconButton>
              <IconButton
                  v-if="viewingEnvelope.status === 'sent' && canEditResource(viewingEnvelope)"
                  id="envelope-status-reminder-cta"
                  @click="openResendNowModal"
                  data-ec="envelope_status"
                  data-et="controls_resend_now"
                  class="mb-base w-full"
                  icon="paper-plane"
                  variant="secondary">
                <span>{{ $t('envelope.resend') }}</span>
              </IconButton>
              <IconButton
                  v-if="viewingEnvelope.status === 'sent' && canEditResource(viewingEnvelope)"
                  id="envelope-status-reminder-settings-cta"
                  @click="openRemindersSetModal"
                  :fullwidth="true"
                  data-ec="envelope_status"
                  data-et="controls_reminders"
                  class="mb-base w-full"
                  icon="bell"
                  variant="secondary">
                <span>{{ $t('envelope.reminderSettings') }}</span>
              </IconButton>
              <IconButton
                  v-if="viewingEnvelope.status === 'sent' && canEditResource(viewingEnvelope)"
                  id="envelope-status-expire-settings-cta"
                  @click="openEnvelopeExpireModal"
                  data-ec="envelope_status"
                  data-et="controls_expiry_settings"
                  class="mb-base w-full"
                  icon="clock"
                  variant="secondary">
                <span>{{ $t('envelope.expirySettings') }}</span>
              </IconButton>
              <IconButton
                  v-if="(viewingEnvelope.status === 'sent' || viewingEnvelope.status === 'verify') && canEditResource(viewingEnvelope)"
                  id="envelope-status-cancel-cta"
                  @click="openEnvelopeCancelModal"
                  data-ec="envelope_status"
                  data-et="controls_cancel"
                  class="mb-base w-full"
                  icon="times"
                  variant="secondary">
                <span>{{ $t('envelope.cancelEnvelope') }}</span>
              </IconButton>
              <IconButton
                  id="envelope-status-delete-cta"
                  v-if="viewingEnvelopeIsDeletable"
                  @click="openEnvelopeDeleteModal"
                  data-ec="envelope_status"
                  data-et="controls_remove"
                  class="mb-base w-full"
                  icon="trash"
                  variant="secondary">
                <span>{{ $t('functional.delete') }}</span>
              </IconButton>
              <VSendWidgetEmail
                  v-if="showVerify"
                  variant="secondary"
                  icon="envelope"
                  data-et="envelope_verify"
                  data-ec="envelope_status"
                  class="mb-base w-full"
                  :envelope="viewingEnvelope">
              </VSendWidgetEmail>
            </div>
          </VStickyColumn>
        </div>
      </VGrid>
      <RemindersSetModal
        :show="showRemindersSetModal"
        :envelope="viewingEnvelope"
        :setReminders="reminders"
        :nextRemind="viewingEnvelope.next_remind"
        :remindDays="remindDays"
        @openCancelRemindersModal="showRemindersCancelModal = true;"
        @close="showRemindersSetModal = false;"
      />
      <RemindersCancelModal
        :show="showRemindersCancelModal"
        :envelope="viewingEnvelope"
        :setReminders="reminders"
        @openSetRemindersModal="showRemindersSetModal = true;"
        @close="showRemindersCancelModal = false;"
      />
      <EnvelopeCancelModal
        :envelope="viewingEnvelope"
        :show="showEnvelopeCancelModal"
        @close="showEnvelopeCancelModal = false;"
      />
      <EnvelopeExpireModal
        :envelope="viewingEnvelope"
        :show="showEnvelopeExpireModal"
        @close="showEnvelopeExpireModal = false;"
      />
      <EnvelopeResendNowModal
        :envelope="viewingEnvelope"
        :show="showEnvelopeResendModal"
        @close="showEnvelopeResendModal = false;"
      />
      <EnvelopeDownloadModal
        :envelopeTitle="viewingEnvelope.title"
        :downloadEnvelope="downloadEnvelope"
        :show="showEnvelopeDownloadModal"
        @close="showEnvelopeDownloadModal = false;"
      />
      <EnvelopeShareModal
        :show="showEnvelopeShareModal"
        :envelope="viewingEnvelope"
        @close="showEnvelopeShareModal = false;"
        @saved="showEnvelopeShareModal = false;"
      />
      <EnvelopeDeleteModal
        :show="showEnvelopeDeleteModal"
        :envelope="viewingEnvelope"
        @close="showEnvelopeDeleteModal = false;"
      />
    </div>
    <div v-else>
      <VMessage type="warning" :fullwidth="true">
        <template v-slot:icon>
          <ExclamationTriangleIcon />
        </template>
        <template v-slot:text>
          {{ $t('message.fail.find', { name: 'envelope' }) }}
        </template>
      </VMessage>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import localeDateFormatter from '@/utils/locale-date-formatter';
import VLabel from '@/components/deprecated/atoms/Label.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VTable from '@/components/deprecated/molecules/Table.vue';
import VStickyColumn from '@/components/deprecated/molecules/StickyColumn.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import PartyStatusTable from '@/views/envelope-status/PartyStatusTable.vue';
import VSendWidgetEmail from '@/views/components/ResendWidgetEmailVerificationButton.vue';
import EnvelopeResendNowModal from '@/views/modals/envelope/EnvelopeResendNowModal.vue';
import EnvelopeDownloadModal from '@/views/modals/envelope/EnvelopeDownloadModal.vue';
import EnvelopeShareModal from '@/views/modals/envelope/EnvelopeShareModal.vue';
import EnvelopeDeleteModal from '@/views/modals/envelope/EnvelopeDeleteModal.vue';
import EnvelopeExpireModal from '@/views/modals/envelope/EnvelopeExpireModal.vue';
import EnvelopeCancelModal from '@/views/modals/envelope/EnvelopeCancelModal.vue';
import download from '@/utils/download';
import DownloadFileButton from '@/components/DownloadFileButton.vue';
import IconButton from '@/components/library/buttons/IconButton.vue';
import RemindersSetModal from '@/views/modals/reminder/RemindersSetModal.vue';
import RemindersCancelModal from '@/views/modals/reminder/RemindersCancelModal.vue';
import { ExtendableFeed } from '@/components/library/feeds/ExtendableFeed';
import { CheckIcon, DownloadIcon, ExclamationTriangleIcon, InfoCircleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'EnvelopeStatus',
  components: {
    VLabel,
    VLoadingSpinner,
    VMessage,
    VTable,
    VStickyColumn,
    VGrid,
    PartyStatusTable,
    VSendWidgetEmail,
    RemindersSetModal,
    RemindersCancelModal,
    EnvelopeResendNowModal,
    EnvelopeDownloadModal,
    EnvelopeShareModal,
    EnvelopeDeleteModal,
    EnvelopeExpireModal,
    EnvelopeCancelModal,
    DownloadFileButton,
    IconButton,
    ExtendableFeed,
    DownloadIcon,
    ExclamationTriangleIcon,
    CheckIcon,
    InfoCircleIcon,
  },
  data() {
    return {
      reminders: {
        loading: false,
        radio: null,
        custom: null,
        days: null,
      },
      hasSentVerificationEmail: false,
      party: {},
      envelope: {
        loading: true,
      },
      modalLoading: false,
      auditLog: {
        loading: true,
      },
      envelopeToResend: {},
      showEnvelopeCancelModal: false,
      showEnvelopeShareModal: false,
      showEnvelopeResendModal: false,
      showEnvelopeExpireModal: false,
      showEnvelopeDownloadModal: false,
      showEnvelopeDeleteModal: false,
      showRemindersSetModal: false,
      showRemindersCancelModal: false,
      downloadFieldDataPending: false,
      uploadButtonLoadingArray: [],
    };
  },
  async mounted() {
    try {
      const fingerprint = this.$route.params.envelopeFingerprint;
      await Promise.all([
        this.$store.dispatch('envelopes/GET_ENVELOPE', { fingerprint }),
        this.$store.dispatch('envelopes/GET_ENVELOPE_AUDIT_LOG', { fingerprint }),
      ]);
    } finally {
      this.auditLog.loading = false;
      this.envelope.loading = false;
    }

    if (this.$route.query.download_envelope) {
      this.openDownloadEnvelopeModal();
    }
  },
  computed: {
    ...mapGetters({
      viewingEnvelope: 'envelopes/viewing',
      viewingLog: 'envelopes/viewingLog',
      userHasRole: 'roles/userHasRole',
      canEditResource: 'envelopes/canEditResource',
    }),
    sharedWithEveryone() {
      return (this.viewingEnvelope.sharing ? !!this.viewingEnvelope.everyone_permission : null);
    },
    sharedGroups() {
      return (this.viewingEnvelope.sharing ? this.viewingEnvelope.sharing.groups : []);
    },
    sharedUsers() {
      return (this.viewingEnvelope.sharing ? this.viewingEnvelope.sharing.users : []);
    },
    showEditSharing() {
      return this.canEditResource(this.viewingEnvelope);
    },
    remindDays() {
      if (this.viewingEnvelope.auto_remind_hours > 0) {
        return this.viewingEnvelope.auto_remind_hours / 24;
      }
      return null;
    },
    showVerify() {
      return this.viewingEnvelope.status === 'verify';
    },
    expiryDate() {
      return localeDateFormatter('date-field', this.viewingEnvelope.next_expire);
    },
    firstPageImg() {
      return this.viewingEnvelope.documents[0] ? this.viewingEnvelope.documents[0].images[0] : '';
    },
    viewingEnvelopeIsDownloadable() {
      return ['signed', 'sent', 'cancelled', 'expired'].includes(this.viewingEnvelope?.status);
    },
    viewingEnvelopeIsDeletable() {
      return this.canEditResource(this.viewingEnvelope) &&
          ['cancelled', 'expired', 'rejected', 'draft'].includes(this.viewingEnvelope?.status);
    },
  },
methods: {
    uploadHasContent(field) {
      if (!!field.upload && field.upload !== '[]') return true;
      return false;
    },
    fieldValueFormatter(field) {
      if (field.type === 'checkbox') {
        return field.content === '1' ? 'true' : 'false';
      } else if (field.type === 'signature' && field.is_completed) {
        return this.$t('envelope.fieldValue.signed');
      } else if (field.type === 'upload' && this.uploadHasContent(field)) {
        return '';
      } else if (field.content === '' || field.content === undefined) {
        return this.$t('envelope.fieldValue.empty');
      }

      return field.content;
    },
    titleFormatter(field) {
      if (field.merge_field !== '') return field.merge_field;
      return field.title;
    },
    openEditSharingModal() {
      this.showEnvelopeShareModal = true;
    },
    openRemindersSetModal() {
      this.showRemindersSetModal = true;
    },
    openRemindersCancelModal() {
      this.$bus.$emit('closeModal');
      this.$bus.$emit('toggleModal', { name: 'reminders-cancel-modal', active: true });
    },
    openEnvelopeExpireModal() {
      this.showEnvelopeExpireModal = true;
    },
    openEnvelopeCancelModal() {
      this.showEnvelopeCancelModal = true;
    },
    openResendNowModal() {
      this.showEnvelopeResendModal = true;
    },
    openEnvelopeDeleteModal() {
        this.showEnvelopeDeleteModal = true;
    },
    openDownloadEnvelopeModal() {
      if (this.viewingEnvelopeIsDownloadable) {
        this.$router.replace({ query: null });
        this.showEnvelopeDownloadModal = true;
      } else {
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.download', { name: 'envelope' }),
          icon: 'times',
          type: 'error',
        }));
      }
    },
    downloadFieldData() {
      this.downloadFieldDataPending = true;

      download({
        url: 'download/field-data',
        params: {
          fingerprint: this.viewingEnvelope.fingerprint,
        },
        fileName: `${this.viewingEnvelope.title}-field-data`,
      }).finally(() => {
        this.downloadFieldDataPending = false;
      });
    },
    downloadEnvelope() {
      download({
        url: 'download/envelope',
        params: {
          fingerprint: this.viewingEnvelope.fingerprint,
        },
        fileName: `${this.viewingEnvelope.title}-envelope`,
      });
    },
    downloadUploadFieldFiles(fieldId) {
      if (!this.uploadButtonLoadingArray.includes(fieldId)) {
        this.uploadButtonLoadingArray.push(fieldId);

        download({
          url: 'download/upload-field',
          params: {
            field_id: fieldId,
          },
          fileName: `${this.viewingEnvelope.title}-${fieldId}-files`,
        }).finally(() => {
          this.uploadButtonLoadingArray.splice(this.uploadButtonLoadingArray.indexOf(fieldId), 1);
        });
      }
    },
  },
};
</script>

<style scoped>
.el-table button.is-circle {
  margin-left: 5px;
}

.p-envelope-status__log {
  border: 1px var(--color-border) solid;
  padding: var(--spacing-base);
  background: #fff;
}

.p-envelope-status__log__utc-toggle {
  text-align: right;
  font-weight: bold;
}

.p-envelope-status__log__utc-toggle__label, .p-envelope-status__log__utc-toggle .a-checkbox {
  vertical-align: middle;
  font-size: var(--font-size-small);
}

.p-envelope-status__log__utc-toggle .a-checkbox {
  margin-left: var(--spacing-xs);
}

.p-envelope-status__controls .a-button {
  margin-bottom: var(--spacing-small);
  display: block;
  white-space: pre;
}

.p-envelope-status__status {
  display: inline-block;
}

.p-envelope-status__preview {
  border: 1px solid var(--color-border);
  border-radius: var(--spacing-xs);
  -webkit-box-shadow: 3px 3px 10px -2px var(--color-border);
  box-shadow: 3px 3px 10px -2px var(--color-border);
  margin-top: -88px;
}

.p-envelope-status--header-details ::v-deep .a-message {
  vertical-align: middle;
}

.p-envelope-status--header-details ::v-deep .a-label {
  margin-right: var(--spacing-small);
  padding: var(--spacing-xs) var(--spacing-base);
}

.p-envelope-status__log-item {
  font-size: var(--font-size-small);
  line-height: 1.5;
  border-top: 1px dashed var(--color-border);
  padding: 5px 0;
}

.p-envelope-status__log-item:nth-of-type(1) {
  border-top: none;
}

.p-envelope-status ::v-deep .m-table__td--type {
  text-transform: capitalize;
}

@media (max-width: 1020px) {
  .p-envelope-status__preview {
    margin-top: 0;
  }
}
</style>
